.picker {
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* Height budget (2026-10b §5.3): up to 12 projects fit without scrolling. */
  max-height: min(640px, calc(100dvh - 24px));
}

/* Up to six active projects: one column, 320px panel (294px content inside the popover). */
.single {
  width: 294px;
}

/* Seven or more: two columns, 360px panel (334px content, 163px per column, §5.3). */
.twoCol {
  width: 334px;
}

/* The warning shake of a dirty create context (§5.4); none under reduced motion. */
.picker[data-shake] {
  animation: picker-shake 200ms ease-out;
}

@keyframes picker-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-6px);
  }
  75% {
    transform: translateX(6px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .picker[data-shake] {
    animation: none;
  }
}

.header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  min-height: 18px;
}

.title {
  font-size: var(--font-size);
  font-weight: 600;
}

.range {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
}

.hint {
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* The project list is the only region that scrolls when the panel hits its height cap. */
.listWrap {
  overflow-y: auto;
  min-height: 120px;
}

.list {
  display: grid;
  /* minmax(0, ...): a long project name shrinks to an ellipsis instead of widening the column. */
  grid-template-columns: minmax(0, 1fr);
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.twoCol .list {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px 8px;
}

.project {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: max(36px, var(--control-h));
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  text-align: left;
}

/* A top-level project and, when it has children, its expand control beside it (project hierarchy). */
.projectRow {
  display: flex;
  align-items: stretch;
  gap: 4px;
}

.projectRow > .project {
  flex: 1;
}

.expandToggle {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: 2px;
  min-height: max(36px, var(--control-h));
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.expandToggle:hover {
  background: var(--surface-muted);
}

.expandToggleOpen svg {
  transform: rotate(180deg);
}

.expandCount {
  font-variant-numeric: tabular-nums;
}

/* Child rows span the whole list width (also in the two-column list) and are indented under their parent. */
.childItem {
  grid-column: 1 / -1;
  margin-left: 18px;
}

.earlierToggle {
  padding: 4px 8px;
  border: 0;
  background: transparent;
  color: var(--accent-700);
  font-size: var(--font-size-sm);
}

.project:hover {
  background: var(--surface-muted);
}

.current {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.dot {
  flex: none;
  width: 12px;
  height: 12px;
  border-radius: var(--radius-xs);
  background: var(--dot-color);
}

/* A child project's swatch is one shade lighter than its parent's (design 2026-10-15 §1). */
.childItem .dot {
  background: color-mix(in srgb, var(--dot-color) 60%, var(--white));
}

.projectName {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.category {
  flex: none;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.empty {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: var(--text-muted);
}

/* The permanent link to Settings → Projects below the project list or dropdown (2026-10c §3). */
.manageLink {
  display: inline-block;
  align-self: flex-start;
  margin-top: 6px;
  padding: 4px 2px;
  color: var(--accent-700);
  font-size: var(--font-size-sm);
}

/* ---- "Change project" of an existing block (2026-10c §3): button + dropdown below the note ---- */
.changeWrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.changeButton {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: max(36px, var(--control-h));
  padding: 0 10px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--surface);
  text-align: left;
}

.changeButton:hover,
.changeButtonOpen {
  background: var(--surface-muted);
}

.changeLabel {
  flex: none;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.changeButton svg {
  flex: none;
  color: var(--text-muted);
}

.dropdown {
  display: flex;
  flex-direction: column;
  padding: 4px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
}

.dropdownList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 240px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
}

.dropdownEmpty {
  margin: 0;
  padding: 6px;
  color: var(--text-muted);
}

.option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  min-height: max(34px, var(--control-h));
  padding: 0 8px;
  border: 0;
  border-radius: var(--radius-xs);
  background: transparent;
  text-align: left;
}

.option:hover {
  background: var(--surface-muted);
}

.optionCurrent {
  background: var(--accent-soft);
}

.optionCheck {
  display: inline-flex;
  flex: none;
  color: var(--accent-700);
}

/* The number key that chooses the project: only where a keyboard is likely (a fine pointer). */
.shortcut {
  display: none;
  flex: none;
  min-width: 14px;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
  text-align: right;
}

@media (pointer: fine) {
  .shortcut {
    display: inline;
  }
}

/* ---- always-visible field area (2026-10b §5.3): time / title / note ---- */

.fields {
  display: flex;
  flex-direction: column;
  flex: none;
  gap: 8px;
  padding: 8px 0;
  border-top: 1px solid var(--border);
}

.fieldRow {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.fieldLabel {
  flex: none;
  width: 40px;
  padding-top: 8px;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.timeRow {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
}

.timeDash {
  color: var(--text-muted);
}

/* Live duration beside the time fields (§3.8). */
.duration {
  min-width: 0;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.durationError {
  color: var(--danger);
}

.inputWrap {
  position: relative;
  flex: 1;
  min-width: 0;
}

.textInput {
  width: 100%;
  min-height: 32px;
  padding: 5px 28px 5px 10px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--surface);
  font-size: var(--font-size);
  resize: none;
}

.textInput::placeholder {
  color: var(--text-muted);
}

.textInput:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: -1px;
}

.noteInput {
  line-height: 1.4;
}

/* The "×" clear of a field (§5.4): clearing is one undoable modification. */
.clear {
  position: absolute;
  top: 4px;
  right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  min-height: 24px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-xs);
  background: transparent;
  color: var(--text-muted);
  font-size: 16px;
  line-height: 1;
}

.clear:hover {
  background: var(--surface-muted);
  color: var(--text);
}

/* "n/40" and "n/200" (§5.3 counters), shown close to the limit. */
.counter {
  position: absolute;
  right: 6px;
  bottom: -14px;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
}

.counterMax {
  color: var(--danger);
}

.fieldHint {
  margin: 0;
  padding-left: 48px;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

/* "Nothing saved yet" notice of a dirty create context (§5.4). */
.notice {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: var(--text);
  font-size: var(--font-size-sm);
}

.noticeIcon {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--accent);
  color: var(--white);
  font-size: 10px;
  font-weight: 700;
}

/* ---- action rows (§5.3 ④): full-width quiet rows, 36px each ---- */

.actions {
  display: flex;
  flex-direction: column;
  flex: none;
  border-top: 1px solid var(--border);
  padding-top: 4px;
}

.actionRow {
  display: block;
  width: 100%;
  min-height: 36px;
  padding: 0 10px;
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--accent);
  font-size: var(--font-size-sm);
  text-align: left;
  cursor: pointer;
}

.actionRow:hover {
  background: var(--surface-muted);
}

.dangerRow {
  color: var(--danger);
}

/* ---- footer: 「取消」 then the primary 「保存」 (or 「确认」 for a draft), right-aligned ---- */

.footer {
  display: flex;
  flex: none;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
}

/* ---- overlap popover (ui-redesign §3.8): one bordered row per overlapping block ---- */

.resolver {
  width: 234px;
}

.overlapRow {
  padding-top: 8px;
  margin-top: 8px;
  border-top: 1px solid var(--border);
}

/* One full-width button per choice. */
.conflictActions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.conflictButton {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
}

@media (max-width: 767px) {
  .single,
  .twoCol {
    width: auto;
  }

  .list,
  .twoCol .list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .textInput {
    min-height: 44px;
    font-size: 16px;
  }

  .clear {
    top: 10px;
    min-width: 44px;
    min-height: 24px;
  }

  .actionRow {
    min-height: 48px;
  }
}

/* Children inside the "change project" dropdown. */
.optionChild {
  padding-left: 26px;
}

.optionChild .dot {
  background: color-mix(in srgb, var(--dot-color) 60%, var(--white));
}

.optionEarlier {
  padding-left: 26px;
  color: var(--accent-700);
  font-size: var(--font-size-sm);
}
